// Direct-Booking Coupon toast — appears bottom-right on 2nd+ visit.
// Reclaims OTA-commission margin by offering returning visitors a personal 10% code
// they can quote to Reception on WhatsApp to book direct.
import React, { useEffect, useMemo, useState } from 'react';
import { Gift, Copy, Check, X, MessageCircle } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Visitor id — anonymous, stored in localStorage forever.
const getOrCreateVisitorId = () => {
  try {
    let v = localStorage.getItem('lrn_visitor_id');
    if (!v) {
      v = 'v-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      localStorage.setItem('lrn_visitor_id', v);
    }
    return v;
  } catch { return 'v-anon'; }
};

const incrementVisitCount = () => {
  try {
    const n = parseInt(localStorage.getItem('lrn_visit_count') || '0', 10) + 1;
    localStorage.setItem('lrn_visit_count', String(n));
    return n;
  } catch { return 1; }
};

// Localized copy — same 7 languages the rest of the site supports.
const COPY = {
  en: {
    eyebrow: "Welcome back",
    title: (pct, tier) => tier === 'loyalty' ? `You're a returning guest — here's ${pct}% off` : `Here's your personal ${pct}% off`,
    body: "Book direct with us and save on your next stay. Quote the code to our team on WhatsApp — we'll apply the discount instantly.",
    copy: "Copy code",
    copied: "Copied!",
    whatsapp: "Redeem on WhatsApp",
    dismiss: "Not now",
    expiresLabel: "Valid until",
    whatsappMsg: (code, pct) => `Hi Hotel La Rosa Negra! I'd like to book direct with my ${pct}% discount code: ${code}`,
  },
  me: {
    eyebrow: "Dobrodošli nazad",
    title: (pct, tier) => tier === 'loyalty' ? `Kao stalni gost dobijate ${pct}% popusta` : `Vaš lični popust od ${pct}%`,
    body: "Rezervišite direktno i uštedite. Pošaljite ovaj kod našem timu na WhatsApp — popust ćemo primijeniti odmah.",
    copy: "Kopiraj kod",
    copied: "Kopirano!",
    whatsapp: "Iskoristi na WhatsApp",
    dismiss: "Ne sada",
    expiresLabel: "Važi do",
    whatsappMsg: (code, pct) => `Zdravo Hotel La Rosa Negra! Želim direktnu rezervaciju sa mojim ${pct}% popust kodom: ${code}`,
  },
  sq: {
    eyebrow: "Mirë se u ktheve",
    title: (pct, tier) => tier === 'loyalty' ? `Si mysafir i kthyer, ${pct}% zbritje` : `Zbritja jote personale ${pct}%`,
    body: "Rezervo direkt me ne dhe kurse në qëndrimin tënd. Dërgojani kodin ekipit tonë në WhatsApp dhe do ta aplikojmë menjëherë.",
    copy: "Kopjo kodin",
    copied: "U kopjua!",
    whatsapp: "Përdore në WhatsApp",
    dismiss: "Jo tani",
    expiresLabel: "Vlen deri",
    whatsappMsg: (code, pct) => `Përshëndetje Hotel La Rosa Negra! Dua të rezervoj direkt me kodin tim të zbritjes ${pct}%: ${code}`,
  },
  de: {
    eyebrow: "Willkommen zurück",
    title: (pct, tier) => tier === 'loyalty' ? `Für Stammgäste: ${pct}% Rabatt` : `Ihre persönlichen ${pct}% Rabatt`,
    body: "Buchen Sie direkt bei uns und sparen Sie. Senden Sie den Code unserem Team per WhatsApp — wir wenden den Rabatt sofort an.",
    copy: "Code kopieren",
    copied: "Kopiert!",
    whatsapp: "Auf WhatsApp einlösen",
    dismiss: "Nicht jetzt",
    expiresLabel: "Gültig bis",
    whatsappMsg: (code, pct) => `Hallo Hotel La Rosa Negra! Ich möchte direkt buchen mit meinem ${pct}% Rabattcode: ${code}`,
  },
  ru: {
    eyebrow: "С возвращением",
    title: (pct, tier) => tier === 'loyalty' ? `Для постоянных гостей: скидка ${pct}%` : `Ваша персональная скидка ${pct}%`,
    body: "Бронируйте напрямую у нас и экономьте. Отправьте код нашей команде в WhatsApp — мы применим скидку сразу.",
    copy: "Скопировать код",
    copied: "Скопировано!",
    whatsapp: "Использовать в WhatsApp",
    dismiss: "Не сейчас",
    expiresLabel: "Действует до",
    whatsappMsg: (code, pct) => `Здравствуйте, Hotel La Rosa Negra! Хочу забронировать напрямую по моему коду скидки ${pct}%: ${code}`,
  },
  pl: {
    eyebrow: "Witamy ponownie",
    title: (pct, tier) => tier === 'loyalty' ? `Dla stałych gości: ${pct}% zniżki` : `Twój osobisty rabat ${pct}%`,
    body: "Zarezerwuj bezpośrednio i oszczędzaj. Prześlij kod naszemu zespołowi na WhatsApp — od razu naliczymy zniżkę.",
    copy: "Kopiuj kod",
    copied: "Skopiowano!",
    whatsapp: "Zrealizuj na WhatsApp",
    dismiss: "Nie teraz",
    expiresLabel: "Ważny do",
    whatsappMsg: (code, pct) => `Cześć Hotel La Rosa Negra! Chciałbym zarezerwować bezpośrednio z moim kodem rabatowym ${pct}%: ${code}`,
  },
  hu: {
    eyebrow: "Üdv újra nálunk",
    title: (pct, tier) => tier === 'loyalty' ? `Visszatérő vendégeknek: ${pct}% kedvezmény` : `Az Ön személyes ${pct}% kedvezménye`,
    body: "Foglaljon közvetlenül nálunk és spóroljon. Küldje el a kódot csapatunknak WhatsApp-on — azonnal levonjuk a kedvezményt.",
    copy: "Kód másolása",
    copied: "Másolva!",
    whatsapp: "Beváltás WhatsApp-on",
    dismiss: "Most nem",
    expiresLabel: "Érvényes",
    whatsappMsg: (code, pct) => `Üdv Hotel La Rosa Negra! Szeretnék közvetlenül foglalni a ${pct}% kedvezménykódommal: ${code}`,
  },
};

const DirectBookingCoupon = () => {
  const { hotelInfo } = useHotel();
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.en;

  const [coupon, setCoupon] = useState(null);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Never show on admin/booking flows or to crawlers.
    const path = window.location.pathname || '';
    if (path.startsWith('/admin') || path.startsWith('/shift-scan') || path.includes('/booking-confirmed') || path.startsWith('/redeem') || path.includes('/terrace/') || path.includes('/inroom/') || path.startsWith('/partner/')) return;  // staff scan page: no guest popup  // guests already in-house: no win-back popup  // B2B partner portal
    if (/bot|crawl|spider|gptbot|chatgpt|claude|perplexity|google-extended/i.test(navigator.userAgent || '')) return;

    // Only offer to returning visitors AND respect dismissal.
    let dismissed = false;
    try { dismissed = !!localStorage.getItem('lrn_coupon_dismissed'); } catch { /* noop */ }
    if (dismissed) return;

    const visits = incrementVisitCount();
    if (visits < 2) return;   // First visit → no toast.

    const vid = getOrCreateVisitorId();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/coupons/issue`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitor_id: vid, lang, source: 'returning_visitor_toast' }),
        });
        if (!res.ok) throw new Error('issue failed');
        const json = await res.json();
        setCoupon(json);
        // Fade-in after 200ms for a smooth appear.
        setTimeout(() => setVisible(true), 200);
      } catch (e) {
        setError(e.message);
      }
    }, 6000);   // 6-second delay after page ready — non-intrusive.

    return () => clearTimeout(timer);
  }, [lang]);

  const dismiss = () => {
    try { localStorage.setItem('lrn_coupon_dismissed', '1'); } catch {}
    setVisible(false);
  };

  const doCopy = async () => {
    if (!coupon) return;
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {}
  };

  const waHref = useMemo(() => {
    if (!coupon || !hotelInfo?.whatsapp) return '#';
    const redeemUrl = `${window.location.origin}/redeem/${coupon.code}`;
    const msg = encodeURIComponent(`${t.whatsappMsg(coupon.code, coupon.discount_pct)}\n${redeemUrl}`);
    return `https://wa.me/${hotelInfo.whatsapp}?text=${msg}`;
  }, [coupon, hotelInfo, t]);

  if (error || !coupon) return null;

  const expiresLabel = new Date(coupon.expires_at).toLocaleDateString(
    lang === 'en' ? 'en-GB' : (lang === 'me' ? 'sr-Latn' : lang),
    { day: '2-digit', month: 'short', year: 'numeric' }
  );

  return (
    <div
      className={`fixed z-50 bottom-4 left-4 sm:left-auto sm:right-4 max-w-sm w-[calc(100vw-2rem)] sm:w-96 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'
      }`}
      role="dialog"
      aria-live="polite"
      data-testid="direct-booking-coupon"
    >
      <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-rose-600 text-white rounded-2xl shadow-2xl overflow-hidden relative">
        {/* Decorative blob */}
        <div className="absolute -top-14 -right-14 w-40 h-40 bg-white/15 rounded-full blur-2xl" aria-hidden="true" />

        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-2.5 right-2.5 p-1.5 rounded-full hover:bg-white/20 transition"
          aria-label={t.dismiss}
          data-testid="coupon-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-5">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange-100">
            <Gift className="w-4 h-4" /> {t.eyebrow}
          </div>
          <h3 className="mt-1.5 text-lg font-bold leading-tight">{t.title(coupon.discount_pct, coupon.loyalty_tier)}</h3>
          <p className="mt-2 text-sm text-orange-50/90 leading-relaxed">{t.body}</p>
          {coupon.perk && (
            <div className="mt-3 bg-yellow-400/25 border border-yellow-200/40 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="coupon-perk">
              <span className="text-lg">🌟</span>
              <p className="text-xs font-semibold text-yellow-50">{coupon.perk}</p>
            </div>
          )}

          {/* Code chip */}
          <div className="mt-4 bg-white/15 backdrop-blur border border-white/25 rounded-lg p-3 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest text-orange-100 font-semibold">Code</p>
              <p className="font-mono text-xl font-bold tracking-wider select-all" data-testid="coupon-code">{coupon.code}</p>
              <p className="text-[10px] text-orange-100/80 mt-0.5">{t.expiresLabel} {expiresLabel}</p>
            </div>
            <button
              onClick={doCopy}
              className="flex items-center gap-1 bg-white text-orange-600 hover:bg-orange-50 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wide transition"
              data-testid="coupon-copy-btn"
            >
              {copied ? <><Check className="w-3.5 h-3.5" /> {t.copied}</> : <><Copy className="w-3.5 h-3.5" /> {t.copy}</>}
            </button>
          </div>

          <div className="mt-3 flex gap-2">
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 rounded-md px-3 py-2.5 text-sm font-semibold shadow-md transition"
              data-testid="coupon-whatsapp-btn"
              onClick={() => {
                // Mark dismissed so we don't nag after they click.
                try { localStorage.setItem('lrn_coupon_dismissed', '1'); } catch {}
              }}
            >
              <MessageCircle className="w-4 h-4" /> {t.whatsapp}
            </a>
            <button
              onClick={dismiss}
              className="px-3 py-2.5 text-xs font-semibold text-orange-100 hover:text-white hover:bg-white/10 rounded-md transition"
              data-testid="coupon-dismiss-btn"
            >
              {t.dismiss}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectBookingCoupon;
